/**
 * React Query hooks for daily spin gamification
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../services/api';
import { useAuthStore } from '../../../../stores/auth-store';
import type { SpinStatus, SpinResult, MultiplierInfo } from '../types';
import { CACHE_TIMES } from '../utils/constants';
import { rumiosKeys } from './useRumios';

/**
 * Query keys for daily spin
 */
export const spinKeys = {
  all: ['spin'] as const,
  status: () => [...spinKeys.all, 'status'] as const,
  multipliers: () => [...spinKeys.all, 'multipliers'] as const,
  history: (limit?: number) => [...spinKeys.all, 'history', limit] as const,
};

/**
 * Fetch daily spin status
 */
export function useSpinStatus() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: spinKeys.status(),
    queryFn: async (): Promise<SpinStatus> => {
      const response = await api.getSpinStatus();
      return {
        canSpin: response.can_spin,
        lastSpinDate: response.last_spin_date,
        lastSpinResult: response.last_spin_result,
        totalSpins: response.total_spins,
        totalRumiosWon: response.total_rumios_won,
        multipliers: {
          hasExtension: response.multipliers.has_extension,
          hasApp: response.multipliers.has_app,
          totalMultiplier: response.multipliers.total_multiplier,
        },
      };
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.SPIN_STATUS,
    gcTime: CACHE_TIMES.SPIN_STATUS * 2,
    // Refetch on window focus to check if day changed
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch multiplier info
 */
export function useSpinMultipliers() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: spinKeys.multipliers(),
    queryFn: async (): Promise<MultiplierInfo> => {
      const response = await api.getSpinMultipliers();
      return {
        hasExtension: response.has_extension,
        hasApp: response.has_app,
        totalMultiplier: response.total_multiplier,
        breakdown: response.breakdown,
      };
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.MULTIPLIERS,
    gcTime: CACHE_TIMES.MULTIPLIERS * 2,
  });
}

/**
 * Fetch spin history
 */
export function useSpinHistory(limit = 10) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: spinKeys.history(limit),
    queryFn: async () => {
      const response = await api.getSpinHistory(limit);
      return response.spins;
    },
    enabled: Boolean(user),
    staleTime: CACHE_TIMES.SPIN_HISTORY,
    gcTime: CACHE_TIMES.SPIN_HISTORY * 2,
  });
}

/**
 * Mutation to execute daily spin
 */
export function useExecuteSpin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<SpinResult> => {
      const response = await api.executeSpin();
      // Transform API response to match SpinResult type
      // Handle new_balance being either a number (unified) or object (old format)
      const newBalance = typeof response.new_balance === 'number'
        ? response.new_balance
        : (response.new_balance?.super_rumios || 0) +
          (response.new_balance?.fresh_rumios || 0) +
          (response.new_balance?.standard_rumios || 0);
      return {
        prize_type: response.prize_type as SpinResult['prize_type'],
        rumios_won: response.rumios_won,
        xp_won: response.xp_won,
        multiplier: response.multiplier,
        base_rumios: response.base_rumios,
        new_balance: newBalance,
        new_xp: response.new_xp,
        new_level: response.new_level,
      };
    },
    onSuccess: (data: SpinResult) => {
      // Update spin status (can no longer spin today)
      queryClient.setQueryData(spinKeys.status(), (old: SpinStatus | undefined) => {
        if (!old) return old;
        return {
          ...old,
          canSpin: false,
          lastSpinDate: new Date().toISOString().split('T')[0],
          lastSpinResult: {
            prize_type: data.prize_type,
            rumios_won: data.rumios_won,
            xp_won: data.xp_won,
            multiplier: data.multiplier,
          },
          totalSpins: old.totalSpins + 1,
          totalRumiosWon: old.totalRumiosWon + data.rumios_won,
        };
      });

      // Update unified Rumio balance
      queryClient.setQueryData(rumiosKeys.balance(), (old: unknown) => {
        if (!old) return old;
        const balance = old as { balance: number; tier_costs: { super: number; fresh: number; standard: number } };
        return {
          ...balance,
          balance: data.new_balance,
        };
      });

      // Invalidate history
      queryClient.invalidateQueries({ queryKey: spinKeys.history() });
    },
  });
}

/**
 * Mutation to update multipliers (extension/app detection)
 */
export function useUpdateMultipliers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { has_extension?: boolean; has_app?: boolean }) =>
      api.updateSpinMultipliers(params),
    onSuccess: (data) => {
      // Update multipliers in cache
      queryClient.setQueryData(spinKeys.multipliers(), (old: MultiplierInfo | undefined) => {
        if (!old) return old;
        return {
          ...old,
          hasExtension: data.has_extension,
          hasApp: data.has_app,
          totalMultiplier: data.total_multiplier,
        };
      });

      // Also update in status
      queryClient.setQueryData(spinKeys.status(), (old: SpinStatus | undefined) => {
        if (!old) return old;
        return {
          ...old,
          multipliers: {
            hasExtension: data.has_extension,
            hasApp: data.has_app,
            totalMultiplier: data.total_multiplier,
          },
        };
      });
    },
  });
}

/**
 * Hook to detect browser extension
 * Checks for window.__RUMIO_EXTENSION_VERSION__
 */
export function useExtensionDetection() {
  const [hasExtension, setHasExtension] = useState(false);
  const { mutate: updateMultipliers } = useUpdateMultipliers();

  useEffect(() => {
    // Check if extension is installed
    const checkExtension = () => {
      const extensionVersion = (window as { __RUMIO_EXTENSION_VERSION__?: string }).__RUMIO_EXTENSION_VERSION__;
      const detected = Boolean(extensionVersion);
      setHasExtension(detected);

      if (detected) {
        updateMultipliers({ has_extension: true });
      }
    };

    // Check immediately
    checkExtension();

    // Also check after a delay (extension might inject later)
    const timer = setTimeout(checkExtension, 1000);

    return () => clearTimeout(timer);
  }, [updateMultipliers]);

  return hasExtension;
}

/**
 * Slot machine animation state management
 */
export function useSlotMachineAnimation() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<SpinResult | null>(null);

  const startSpin = useCallback((spinResult: SpinResult) => {
    setIsSpinning(true);
    setShowResult(false);
    setResult(spinResult);

    // Animation duration: 2 seconds for spinning + 0.5s delay for result
    setTimeout(() => {
      setIsSpinning(false);
      setTimeout(() => {
        setShowResult(true);
      }, 500);
    }, 2000);
  }, []);

  const reset = useCallback(() => {
    setIsSpinning(false);
    setShowResult(false);
    setResult(null);
  }, []);

  return {
    isSpinning,
    showResult,
    result,
    startSpin,
    reset,
  };
}

/**
 * Get countdown until next spin is available
 */
export function useNextSpinCountdown(canSpin: boolean) {
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    if (canSpin) {
      // Intentionally setting state when prop changes to reset timer
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTimeLeft('');
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const diffMs = tomorrow.getTime() - now.getTime();

      if (diffMs <= 0) {
        setTimeLeft('');
        return;
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    // Calculate immediately
    calculateTimeLeft();

    // Update every second
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [canSpin]);

  return timeLeft;
}
